// Typed client for the racing-api Betfair betting endpoints.
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { authHeader } from './auth';
import {isPaperAccount,paperStatus,paperRequest} from './paper';

const base = `${API_BASE_URL}/api/betfair`;

// All Betfair routes require auth — send the Bearer token on every call.
const cfg = () => ({ headers: { ...authHeader() } });

export interface BetfairAccount {
  balance: number;
  exposure: number;
  exposureLimit: number;
  discountRate: number;
  pointsBalance: number;
  currency: string;
}

export interface BetfairStatus {
  configured: boolean;
  mode: 'cert' | 'password';
  account: BetfairAccount;
  minStake: number;
}

export type Side = 'BACK' | 'LAY';
export type Persistence = 'LAPSE' | 'PERSIST' | 'MARKET_ON_CLOSE';

export interface PlaceBetRequest {
  marketId: string;
  selectionId: number;
  runnerName?: string;
  side: Side;
  size: number;
  price: number;
  persistenceType?: Persistence;
  handicap?: number;
  meetingName?: string;
  raceNumber?: number;
  marketName?: string;
  date?: string;
}

export interface PlacedBet {
  betId: string | null;
  customerRef: string;
  orderStatus: string;
  sizeMatched: number;
  averagePriceMatched: number | null;
  requestedPrice: number;
  stake: number;
  side: Side;
  runnerName?: string;
}

export interface BetRecord {
  _id: string;
  bet_id: string | null;
  market_id: string;
  market_name?: string | null;
  meeting_name?: string | null;
  race_number?: number | null;
  date?: string | null;
  selection_id: number;
  runner_name?: string | null;
  side: Side;
  requested_price: number;
  matched_price?: number | null;
  stake: number;
  size_matched: number;
  persistence_type: string;
  order_status: string;
  place_status: string;
  error_code?: string | null;
  profit?: number | null;
  outcome?: string | null;
  settled: boolean;
  placed_at: string;
  // Present (2026-09-02 fix) only on rows merged in from the automated rolling/bucket-Kelly
  // engine's own real orders (rolling_offers) — absent on the older manual-placement rows.
  source?: 'rolling_engine';
}

export interface BetsResponse {
  bets: BetRecord[];
  summary: {
    settledProfit: number;
    settledCount: number;
    openStake: number;
    openCount: number;
  };
  // Set when the live Betfair refresh failed (e.g. a session error) — the list is
  // still returned from our records, but possibly stale.
  refreshError?: string | null;
}

// Pull a clean message out of an axios error from these endpoints.
export const betfairErrorMessage = (err: unknown): string => {
  if (axios.isAxiosError(err)) {
    return err.response?.data?.error || err.message || 'Request failed';
  }
  return err instanceof Error ? err.message : 'Request failed';
};

// ── Live market book (fresh prices straight from Betfair) ──
export interface LivePriceSize {
  price: number;
  size: number;
}

export interface LiveBookRunner {
  selectionId: number;
  status?: string;
  lastPriceTraded?: number | null;
  totalMatched?: number;
  sp?: { nearPrice?: number | null; farPrice?: number | null };
  ex?: {
    availableToBack?: LivePriceSize[];
    availableToLay?: LivePriceSize[];
    tradedVolume?: LivePriceSize[];
  };
}

export interface LiveBook {
  marketId: string;
  status?: string;
  inplay?: boolean;
  totalMatched?: number;
  numberOfActiveRunners?: number;
  runners?: LiveBookRunner[];
}

export const getBetfairStatus = async (): Promise<BetfairStatus> =>
  isPaperAccount()?paperStatus():(await axios.get(`${base}/status`, cfg())).data;

export const getLiveMarket = async (marketId: string): Promise<{ book: LiveBook | null }> =>
  isPaperAccount()?paperRequest('/market/'+encodeURIComponent(marketId)):(await axios.get(`${base}/market/${marketId}`, cfg())).data;

const paperSingleRequests=new Map<string,string>();
export const placeBet = async (req: PlaceBetRequest): Promise<{ success: boolean; bet: PlacedBet }> =>
  {if(!isPaperAccount())return (await axios.post(`${base}/place`, req, cfg())).data;
  const key=JSON.stringify(req);let id=paperSingleRequests.get(key);if(!id){id=crypto.randomUUID();paperSingleRequests.set(key,id);}
  const result=await paperRequest('/single',{...req,request_id:id});paperSingleRequests.delete(key);return result;};

export const cancelBet = async (marketId: string, betId: string) =>
  (await axios.post(`${base}/cancel`, { marketId, betId }, cfg())).data;

export const getBets = async (): Promise<BetsResponse> =>
  (await axios.get(`${base}/bets`, cfg())).data;

// ── Per-user account connection ─────────────────────────────────────────────
export interface BetfairConnection {
  connected: boolean;
  username?: string;
  connected_at?: string | null;
}

export interface ConnectResult {
  connected: true;
  username: string;
  account: { balance: number; exposure: number; currency: string };
}

export const getBetfairConnection = async (): Promise<BetfairConnection> =>
  (await axios.get(`${base}/connection`, cfg())).data;

export const connectBetfair = async (
  username: string,
  password: string,
  appKey: string
): Promise<ConnectResult> =>
  (await axios.post(`${base}/connect`, { username, password, appKey }, cfg())).data;

export const disconnectBetfair = async (): Promise<{ disconnected: boolean }> =>
  (await axios.delete(`${base}/connect`, cfg())).data;


// Main-page BACK singles share Auto's manual BSP queue and its existing guards.
const bspSingleRequests=new Map<string,string>();
export const queueBspSingle=async(req:PlaceBetRequest,marketType:'WIN'|'PLACE')=>{
 if(req.side!=='BACK')throw new Error('The manual BSP queue supports BACK singles only.');
 const key=JSON.stringify([req.marketId,req.selectionId,marketType,req.size]);
 let id=bspSingleRequests.get(key);if(!id){id=crypto.randomUUID();bspSingleRequests.set(key,id);}
 const result=(await axios.post(API_BASE_URL+'/api/rolling/simple-bsp/add-manual-bet',{
  expected_mode:'live',market_id:req.marketId,market_type:marketType,
  selection_id:req.selectionId,stake:req.size,allow_duplicate:false,request_id:id
 },cfg())).data;
 if(!result?.queue_id)throw new Error('Auto did not confirm the selection. It has been kept in your slip.');
 bspSingleRequests.delete(key);
 return result;
};
